<template>
  <div>
    <card type="chart">
      <template slot="header">
        <div class="row">
          <div class="col-sm-6 text-left">
            <h2 class="card-title">{{ $t('performanceChart.title') }}</h2>
            <h5 class="card-category">{{ $t('performanceChart.subtitle', {months_count: monthCount}) }}</h5>
          </div>
          <div class="col-sm-6 d-flex d-sm-block">
            <div v-if="error == null"
                 class="btn-group btn-group-toggle float-right"
                 data-toggle="buttons">
              <label
                v-for="(option, index) in bigLineChartCategories"
                :key="option.name"
                class="btn btn-sm btn-primary btn-simple"
                :class="{ active: bigLineChart.activeIndex === index }"
                :id="index">
                <input
                  type="radio"
                  @click="initBigChart(index)"
                  name="options"
                  autocomplete="off"
                  :checked="bigLineChart.activeIndex === index"/>
                <span class="d-none d-sm-block">{{ option.name }}</span>
                <span class="d-block d-sm-none">
                      <i :class="option.icon"></i>
                    </span>
              </label>
            </div>
          </div>
        </div>
      </template>
      <div class="chart-area">
        <LoadingPanel v-if="loading"/>
        <div style="height: 100%" v-else>
          <line-chart v-if="error == null"
                      style="height: 100%"
                      ref="bigChart"
                      :chart-data="bigLineChart.chartData"
                      :gradient-colors="bigLineChart.gradientColors"
                      :gradient-stops="bigLineChart.gradientStops"
                      :extra-options="bigLineChart.extraOptions">
          </line-chart>
          <div v-else>
            <base-alert type="danger">
              <!-- TODO: add error translation -->
              {{ getActionErrorDetails }}
            </base-alert>
          </div>
        </div>
      </div>
    </card>
    <!-- Stats Cards -->
    <div class="row">
      <div class="col-lg-4 col-md-4" v-for="card in statsCards" :key="card.subTitle">
        <stats-card
          :title="card.title"
          :sub-title="card.subTitle"
          :type="card.type"
          :icon="card.icon"
        >
          <template slot="footer">
            {{ card.footer }}
          </template>
        </stats-card>
      </div>
    </div>
  </div>
</template>
<script>
import {BaseAlert, Collapse, CollapseItem, TabPane, Tabs} from 'src/components';
import StatsCard from 'src/components/Cards/StatsCard';
import LineChart from '@/components/Charts/LineChart';
import * as chartConfigs from '@/components/Charts/config';
import config from '@/config';
import {mapActions, mapState} from "vuex";
import LoadingPanel from "src/components/LoadingPanel";

let bigChartDatasetOptions = {
  fill: true,
  borderColor: config.colors.primary,
  borderWidth: 2,
  borderDash: [],
  borderDashOffset: 0.0,
  pointBackgroundColor: config.colors.primary,
  pointBorderColor: 'rgba(255,255,255,0)',
  pointHoverBackgroundColor: config.colors.primary,
  pointBorderWidth: 20,
  pointHoverRadius: 4,
  pointHoverBorderWidth: 15,
  pointRadius: 4,
}

function add(accumulator, a) {
  return accumulator + a;
}

export default {
  name: 'starter-page',
  components: {
    LineChart,
    TabPane,
    Tabs,
    BaseAlert,
    Collapse,
    CollapseItem,
    StatsCard,
    LoadingPanel
  },
  data() {
    return {
      monthCount: 12,
      bigLineChart: {
        activeIndex: 0,
        chartData: {
          datasets: [{
            ...bigChartDatasetOptions,
            // data: bigChartData[0]
            data: []
          }],
          labels: []
          // labels: bigChartLabels
        },
        extraOptions: chartConfigs.purpleChartOptions,
        gradientColors: config.colors.primaryGradient,
        gradientStops: [1, 0.4, 0],
        categories: []
      }
    };
  },
  computed: {
    ...mapState({
      loading: state => state.tickets_stats.loading,
      ticketsStats: state => state.tickets_stats.data,
      error: state => state.tickets_stats.error,
    }),
    getActionErrorDetails() {
      let error = this.error;
      if (error) {
        return error.bodyText == '' ? error.url + ' - ' + error.status : error.bodyText
      }
      return '';
    },
    bigLineChartLabels() {
      let ticketsStats = this.ticketsStats;
      let labels = []
      let months = [this.$t('months.jan3'), this.$t('months.feb3'), this.$t('months.mar3'), this.$t('months.apr3'), this.$t('months.may3'), this.$t('months.jun3'), this.$t('months.jul3'), this.$t('months.aug3'), this.$t('months.sep3'), this.$t('months.oct3'), this.$t('months.nov3'), this.$t('months.dec3')];

      for (let i = 0; i < ticketsStats.length; i++) {
        labels.push(months[ticketsStats[i].month - 1])
      }

      return labels
    },
    bigLineChartCategories() {
      return [
        {name: this.$t("performanceChart.views.singleTickets"), icon: 'tim-icons icon-tap-02'},  // 1
        {name: this.$t("performanceChart.views.monthlyTickets"), icon: 'tim-icons icon-gift-2'},   // 0
        {name: this.$t("performanceChart.views.sales"), icon: 'tim-icons icon-single-02'},        // 2
      ];
    },
    // returns three datasets:
    // 1. monthly counts
    // 2. singles counts
    // 3. cash total
    bigLineChartData() {
      let datasets = []

      // totals per month
      let singlesTotals = []
      let monthlyTotals = []
      let cashTotal = []

      let monthTicketStats = this.ticketsStats;

      for (let i = 0; i < monthTicketStats.length; i++) {
        //
        // Calculate singles
        //
        singlesTotals.push(monthTicketStats[i].single_tickets)

        //
        // Calculate monthly tickets
        //
        monthlyTotals.push(monthTicketStats[i].monthly_tickets)


        //
        // Calculate cash earned
        //
        cashTotal.push((monthTicketStats[i].monthly_ticket_charge + monthTicketStats[i].single_ticket_charge) / 100)
      }

      datasets.push(singlesTotals)
      datasets.push(monthlyTotals)
      datasets.push(cashTotal)

      return datasets
    },
    statsCards() {
      let totalSinglesBought = 0
      let totalMonthlyBought = 0
      let totalSales = 0

      // some additional stats for the cards
      let currentMonthSingles = 0
      let lastMonthMonthlyTickets = 0
      let currentMonthSales = 0

      if (this.bigLineChartData.length > 0) {
        totalSinglesBought = this.bigLineChartData[0].reduce(add, 0)
        // getting last element (current month)
        currentMonthSingles = this.bigLineChartData[0][this.bigLineChartData[0].length - 1];
      }
      if (this.bigLineChartData.length > 0) {
        totalMonthlyBought = this.bigLineChartData[1].reduce(add, 0)
        // getting last month
        lastMonthMonthlyTickets = this.bigLineChartData[1][this.bigLineChartData[1].length - 2];
      }
      if (this.bigLineChartData.length > 0) {
        totalSales = this.bigLineChartData[2].reduce(add, 0)
        // getting current month earnings
        currentMonthSales = this.bigLineChartData[2][this.bigLineChartData[2].length - 1] * 100;
      }

      return [
        {
          title: totalSinglesBought.toString(),
          subTitle: this.$t("performanceChart.singlesCard.title", {months_count: this.monthCount}),
          type: 'warning',
          icon: 'tim-icons icon-user-run',
          footer: this.$t("performanceChart.singlesCard.footerLabel", {count: currentMonthSingles})
        },
        {
          title: totalMonthlyBought.toString(),
          subTitle: this.$t("performanceChart.monthlyCard.title", {months_count: this.monthCount}),
          type: 'primary',
          icon: 'tim-icons icon-badge',
          footer: this.$t("performanceChart.monthlyCard.footerLabel", {count: lastMonthMonthlyTickets})
        },
        {
          title: `€ ${totalSales.toFixed(2).toString()}`,
          subTitle: this.$t("performanceChart.salesCard.title", {months_count: this.monthCount}),
          type: 'info',
          icon: 'tim-icons icon-money-coins',
          footer: this.$t("performanceChart.salesCard.footerLabel", {count: this.$options.filters.price(currentMonthSales)})
        }
      ]
    },
  },
  mounted() {
    this.GetTicketsStatisticsForPeriod(this.monthCount).then(() => {
      if (this.error == null) {
        this.initBigChart(0)
      }
    })
  },
  methods: {
    ...mapActions(['GetTicketsStatisticsForPeriod']),
    initBigChart(index) {
      let chartData = {
        datasets: [{
          ...bigChartDatasetOptions,
          data: this.bigLineChartData[index]
        }],
        labels: this.bigLineChartLabels
      };
      // let foo = this.bigLineChartData
      this.$refs.bigChart.updateGradients(chartData);
      this.bigLineChart.chartData = chartData;
      this.bigLineChart.activeIndex = index;
    }
  }
};
</script>
<style lang="scss"></style>
